import { Fragment, type MouseEvent, type ReactNode } from 'react';
import { flexRender, type RowData } from '@tanstack/react-table';
import type { LegacyRow } from '@tanstack/react-table/legacy';
import { Checkbox } from '@/components/ui/checkbox';
import { TableBody, TableCell, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { actionsCellClass, bodyCellClass, selectCellClass, type PinningContext } from './pinning';
import { rowDetailId } from './types';
import type { VirtualWindow } from './use-virtual-rows';

export interface DataTableBodyProps<TData extends RowData> {
  rows: LegacyRow<TData>[];
  window: VirtualWindow;
  rowHeight: number;
  totalColumns: number;
  pinning: PinningContext;
  focusedIndex: number;
  onFocusRow: (index: number) => void;
  onOpenRow: (row: LegacyRow<TData>, options: { newTab: boolean }) => void;
  openable: boolean;
  rowActions?: (row: TData) => ReactNode;
  rowLabel?: (row: TData) => string;
  rowDetail?: (row: TData) => ReactNode;
  expandedIds?: ReadonlySet<string>;
  /** Ids cuyo contenido llena la columna en vez de pararse en su `size`. */
  fillColumns?: readonly string[];
}

const Spacer = ({ height, columns }: { height: number; columns: number }) =>
  height > 0 ? (
    <tr aria-hidden style={{ height }}>
      <td colSpan={columns} />
    </tr>
  ) : null;

export function DataTableBody<TData extends RowData>({
  rows,
  window: virtual,
  rowHeight,
  totalColumns,
  pinning,
  focusedIndex,
  onFocusRow,
  onOpenRow,
  openable,
  rowActions,
  rowLabel,
  rowDetail,
  expandedIds,
  fillColumns,
}: DataTableBodyProps<TData>) {
  // Roving tabindex: exactly one row is in the tab order, so Tab enters the
  // table once instead of walking every row.
  const tabStop = focusedIndex < 0 ? 0 : focusedIndex;

  return (
    <TableBody>
      <Spacer height={virtual.padTop} columns={totalColumns} />

      {rows.slice(virtual.start, virtual.end).map((row, offset) => {
        const index = virtual.start + offset;
        const selected = row.getIsSelected();
        const abierta = Boolean(rowDetail && expandedIds?.has(row.id));
        // Un rótulo en blanco es lo mismo que no tenerlo: una persona que no dio
        // su nombre dejaba la casilla en «Seleccionar », y una columna entera de
        // casillas con el mismo rótulo a medias no distingue ninguna fila. Esta
        // tabla no sabe de personas —de eso se ocupa quien pasa `rowLabel`—, así
        // que cae en su propio recurso de siempre: el número de fila.
        const etiqueta = rowLabel?.(row.original).trim() || `fila ${index + 1}`;

        return (
          <Fragment key={row.id}>
          <TableRow
            data-row-index={index}
            data-selected={selected}
            aria-selected={pinning.selectable ? selected : undefined}
            tabIndex={index === tabStop ? 0 : -1}
            onFocus={() => onFocusRow(index)}
            onClick={(event: MouseEvent) =>
              openable && onOpenRow(row, { newTab: event.metaKey || event.ctrlKey })
            }
            onAuxClick={(event: MouseEvent) => {
              if (openable && event.button === 1) onOpenRow(row, { newTab: true });
            }}
            style={{ height: rowHeight }}
            className={cn(
              'group/row transition-colors duration-150 ease-out',
              openable && 'cursor-pointer',
              selected ? 'bg-muted' : 'hover:bg-muted/50',
            )}
          >
            {pinning.selectable && (
              <TableCell
                className={selectCellClass(pinning)}
                onClick={(event: MouseEvent) => event.stopPropagation()}
              >
                <Checkbox
                  checked={selected}
                  onCheckedChange={(checked: boolean | 'indeterminate') =>
                    row.toggleSelected(checked === true)
                  }
                  aria-label={`Seleccionar ${etiqueta}`}
                />
              </TableCell>
            )}

            {row.getVisibleCells().map((cell, cellIndex) => (
              <TableCell
                key={cell.id}
                // `max-width: 0` no es el ancho que se dibuja: es lo que esta
                // celda declara para el cálculo de la columna, o sea nada. El
                // ancho lo sigue poniendo el encabezado, y el bloque de adentro
                // llena lo que la columna termine midiendo (ver `fillColumns`).
                style={fillColumns?.includes(cell.column.id) ? { maxWidth: 0 } : undefined}
                className={cn('px-4', bodyCellClass(cellIndex, pinning))}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            ))}

            {rowActions && (
              <TableCell
                className={actionsCellClass}
                onClick={(event: MouseEvent) => event.stopPropagation()}
              >
                {rowActions(row.original)}
              </TableCell>
            )}
          </TableRow>

          {/* Una fila propia a todo el ancho, y no un panel flotante: el detalle
              pertenece a esta fila y tiene que moverse con ella al desplazar.
              No es pulsable —no propaga el clic de apertura— porque su contenido
              se lee y a veces se copia. */}
          {abierta && (
            <TableRow id={rowDetailId(row.id)} className="bg-muted/30 hover:bg-muted/30">
              <TableCell colSpan={totalColumns} className="p-0">
                {rowDetail?.(row.original)}
              </TableCell>
            </TableRow>
          )}
          </Fragment>
        );
      })}

      <Spacer height={virtual.padBottom} columns={totalColumns} />
    </TableBody>
  );
}
